import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { AttachmentNodeView } from './view';
import type { ExtensionMenuConfig } from '@cotera/client/app/components/app/rte/types/extension-config';
import type { SlashCommand } from '@cotera/client/app/components/app/rte/types/extension-interface';
import type { ComboboxItem } from '@cotera/client/v0/components/ui/combobox';

export interface AttachmentOptions extends ExtensionMenuConfig {
  HTMLAttributes: Record<string, any>;
  items?: ComboboxItem[];
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    attachment: {
      /**
       * Insert an attachment block
       */
      setAttachment: (attributes?: {
        value?: string;
        isFreehand?: boolean;
      }) => ReturnType;
    };
  }
}

export const AttachmentNode = Node.create<AttachmentOptions>({
  name: 'attachment',

  addOptions() {
    return {
      HTMLAttributes: {},
      items: [],
    };
  },

  group: 'block',

  atom: true,
  selectable: false,

  addAttributes() {
    return {
      value: {
        default: '',
        parseHTML: (element) => {
          const data = element.getAttribute('data-value');
          return data || '';
        },
        renderHTML: (attributes) => {
          return {
            'data-value': attributes['value'] || '',
          };
        },
      },
      isFreehand: {
        default: false,
        parseHTML: (element) => {
          const data = element.getAttribute('data-is-freehand');
          return data === 'true';
        },
        renderHTML: (attributes) => {
          return {
            'data-is-freehand': attributes['isFreehand'] ? 'true' : 'false',
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="attachment"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { 'data-type': 'attachment' }),
    ];
  },

  addNodeView() {
    return ReactNodeViewRenderer(AttachmentNodeView);
  },

  addCommands() {
    return {
      setAttachment:
        (attributes = {}) =>
        ({ commands }) => {
          // Insert the node and then move cursor after it to avoid selection highlighting
          commands.insertContent(
            {
              type: this.name,
              attrs: attributes,
            },
            {
              updateSelection: false, // <-- This prevents auto-selection
            }
          );

          // Move cursor to after the node to prevent it from being selected
          return commands.setTextSelection(-1);
        },
    };
  },

  // Add slash command configuration
  addStorage() {
    return {
      getSlashConfig: (): SlashCommand => ({
        title: 'Attachment',
        subtext: 'Insert an attachment reference',
        keywords: ['attachment', 'attach', 'file', 'link', 'reference'],
        badge: 'paper-clip',
        group: 'Insert',
        available: (editor) => {
          // Basic checks
          if (!editor.isEditable || !editor.can().setAttachment()) {
            return false;
          }

          return true;
        },
        action: ({ editor }) => {
          editor.chain().focus().setAttachment().run();
        },
      }),
    };
  },
});
